// @mui
import {
  Card,
  Stack,
  Typography,
  CardContent,
  Skeleton,
} from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';

// ----------------------------------------------------------------------

export default function ScoreCard({ score, isLoading }) {
  const mdUp = useResponsive("up", "md");

  if (isLoading) return (
  <Stack direction="row" spacing={{ xs: 1, sm: 1, md: 2.5 }} sx={{ mb: 3 }}>
    <Skeleton variant="rectangular" height={mdUp ? 150 : 120} sx={{ width: 1, borderRadius: 2 }}/>
    <Skeleton variant="rectangular" height={mdUp ? 150 : 120} sx={{ width: 1, borderRadius: 2 }}/>
    <Skeleton variant="rectangular" height={mdUp ? 150 : 120} sx={{ width: 1, borderRadius: 2 }}/>
  </Stack>
  );

  return (
    <>
    {mdUp ? (
    <Stack direction="row" alignItems="center" spacing={2.5} sx={{ mb: 3 }}>
      {/* 1 */}
      <Card sx={{ width: 1, borderRadius: 2, backgroundColor: "grey.100", boxShadow: 0 }}>
        <CardContent sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Iconify icon="eva:email-fill" sx={{ width: 16, height: 16, color: "text.disabled" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              매칭 받은 횟수
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">
              {score?.matching_count}회
            </Typography>
            <DIOScore score={score?.matching_score} />
          </Stack>
        </CardContent>
      </Card>
      {/* 2 */}
      <Card sx={{ width: 1, borderRadius: 2, backgroundColor: "grey.100", boxShadow: 0 }}>
        <CardContent sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SvgIconStyle src={`/icons/icon-arrow-right-circle-mono.svg`} sx={{ width: 16, height: 16, color: "text.disabled" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              크루의 매칭 응답율
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">
              {Math.round((score?.response_ratio || 0) * 100)}%
            </Typography>
            <DIOScore score={score?.response_score} />
          </Stack>
        </CardContent>
      </Card>
      {/* 3 */}
      <Card sx={{ width: 1, borderRadius: 2, backgroundColor: "grey.100", boxShadow: 0, maxHeight: "100%", m: "auto" }}>
        <CardContent sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SvgIconStyle src={`/icons/icon-user-check-mono.svg`} sx={{ width: 16, height: 16, color: "text.disabled" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              회사의 프로필 수락율
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">
              {Math.round((score?.accept_ratio || 0) * 100)}%
            </Typography>
            <DIOScore score={score?.accept_score} />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
    ) : (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3, height: "85px" }}>
        <Card sx={{ width: 1, borderRadius: 1.5, backgroundColor: "grey.100", boxShadow: 0, p: 1.5, height: "100%" }}>
          <Typography variant="caption">
            매칭 받은 횟수
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">
              {score?.matching_count}회
            </Typography>
            <DIOScore score={score?.matching_score} />
          </Stack>
        </Card>
        <Card sx={{ width: 1, borderRadius: 1.5, backgroundColor: "grey.100", boxShadow: 0, p: 1.5, height: "100%" }}>
          <Typography variant="caption">
            매칭 응답율
          </Typography>
          {score?.response_score === "nodata" ? (
            <Stack sx={{ justifyContent: "center", textAlign: "center", my: 1.5 }}>
              <Typography variant="overline" sx={{ color: "text.disabled" }}>
                데이터 없음
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4">
                {Math.round((score?.response_ratio || 0) * 100)}%
              </Typography>
              <DIOScore score={score?.response_score} />
            </Stack>
          )}
        </Card>
        <Card sx={{ width: 1, borderRadius: 1.5, backgroundColor: "grey.100", boxShadow: 0, p: 1.5, height: "100%" }}>
          <Typography variant="caption">
            회사의 수락율
          </Typography>
          {score?.accept_score === "nodata" ? (
            <Stack sx={{ justifyContent: "center", textAlign: "center", my: 1.5 }}>
              <Typography variant="overline" sx={{ color: "text.disabled" }}>
                데이터 없음
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4">
                {Math.round((score?.accept_ratio || 0) * 100)}%
              </Typography>
              <DIOScore score={score?.accept_score} />
            </Stack>
          )}
        </Card>
      </Stack>
    )}
    </>
  )
};

function DIOScore({ score }) {
  const SCORE_ICONS = {
    "nodata": "/icons/icon-minus-circle-mono.svg",
    "verylow": "/icons/icon-exclamation-circle-mono.svg",
    "low": "/icons/icon-exclamation-circle-mono.svg",
    "good": "/icons/icon-minus-circle-mono.svg",
    "high": "/icons/icon-emoji-mono.svg",
  }
  const SCORE_COLORS = {
    "nodata": "text.disabled",
    "verylow": "error.main",
    "low": "error.main",
    "good": "info.main",
    "high": "info.main",
  }
  const SCORE_TEXTS = {
    "nodata": "데이터 없음",
    "verylow": "매우 낮음",
    "low": "낮음",
    "good": "양호",
    "high": "높음",
  }
  const mdUp = useResponsive("up", "md");

  if (!score) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <SvgIconStyle src={SCORE_ICONS[score]} sx={{ width: mdUp ? 14 : 12, height: mdUp ? 14 : 12, color: SCORE_COLORS[score] }} />
      {mdUp && (
      <Typography variant="subtitle2" sx={{ color: SCORE_COLORS[score] }}>
        {SCORE_TEXTS[score]}
      </Typography>
      )}
    </Stack>
  )
};
